import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { login, initializeAuthStorage } from '@/services/authService';
import { useApp } from '@/contexts/AppContext';
import { toast } from '@/hooks/use-toast';
import { apiService } from '@/services/apiService';
import { Loader2, AlertCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authenticateWithApi } from '@/services/userApiService';
import ApiDebugger from '@/components/debug/ApiDebugger';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [useApiLogin, setUseApiLogin] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();
  const { setCurrentUser, currentUser } = useApp();

  useEffect(() => {
    initializeAuthStorage();
    
    // If the user is already logged in, redirect them
    if (currentUser) {
      switch (currentUser.role) {
        case 'admin':
          navigate('/');
          break;
        case 'waiter':
          navigate('/tables');
          break;
        case 'kitchen':
          navigate('/kitchen');
          break;
        case 'delivery':
          navigate('/delivery');
          break;
        default:
          navigate('/');
          break;
      }
      return;
    }
    
    const savedApiUrl = localStorage.getItem('api_base_url');
    if (savedApiUrl) {
      setApiUrl(savedApiUrl);
      setUseApiLogin(true);
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      if (useApiLogin && !apiUrl.trim()) {
        setLoginError('A URL da API é obrigatória para login com a API');
        setIsLoading(false);
        return;
      }

      console.log('Iniciando processo de login com valores:', {
        username,
        useApiLogin,
        apiUrl: apiUrl.trim()
      });

      if (useApiLogin && apiUrl) {
        const normalizedUrl = apiUrl.trim().endsWith('/') 
          ? apiUrl.trim().slice(0, -1) 
          : apiUrl.trim();
        
        console.log(`Configurando URL da API para login: ${normalizedUrl}`);
        localStorage.setItem('api_base_url', normalizedUrl);
        
        await apiService.updateConfig(normalizedUrl, '', undefined);
        
        console.log('Tentando autenticação pela API externa');
        const apiUser = await authenticateWithApi(username, password);
        
        if (apiUser) {
          console.log('Login pela API bem-sucedido:', apiUser);
          setCurrentUser(apiUser);
          
          toast({
            title: 'Login realizado com sucesso',
            description: `Bem-vindo, ${apiUser.name}!`,
          });
          
          switch (apiUser.role) {
            case 'admin':
              navigate('/');
              break;
            case 'waiter':
              navigate('/tables');
              break;
            case 'kitchen':
              navigate('/kitchen');
              break;
            case 'delivery':
              navigate('/delivery');
              break;
            default:
              navigate('/');
              break;
          }
          return;
        } else {
          console.error('Falha na autenticação com a API externa');
          setLoginError('Não foi possível autenticar com a API. Verifique suas credenciais e URL da API.');
          setIsLoading(false);
          return;
        }
      }
      
      console.log('Tentando login local');
      const user = await login(username, password);
      
      console.log('Resultado do login local:', user ? 'Sucesso' : 'Falha');
      
      if (user) {
        setCurrentUser(user);
        
        toast({
          title: 'Login realizado com sucesso',
          description: `Bem-vindo, ${user.name}!`,
        });
        
        switch (user.role) {
          case 'admin':
            navigate('/');
            break;
          case 'waiter':
            navigate('/tables');
            break;
          case 'kitchen':
            navigate('/kitchen');
            break;
          case 'delivery':
            navigate('/delivery');
            break;
          default:
            navigate('/');
            break;
        }
      } else {
        setLoginError('Usuário ou senha incorretos');
      }
    } catch (error) {
      console.error('Erro de login detalhado:', error);
      setLoginError(error instanceof Error 
        ? `Erro: ${error.message}` 
        : 'Ocorreu um erro inesperado ao fazer login');
      
      toast({
        title: 'Erro no login',
        description: 'Ocorreu um erro ao fazer login',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <Card className="w-full max-w-md">
        <div className="flex justify-center mt-6 mb-2">
          <img 
            src="https://www.rspcloud.com.br/Images/Logo_Easy.png" 
            alt="Logo da Empresa" 
            className="h-20 w-auto"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/images/logo.png";
            }}
          />
        </div>
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Login</CardTitle>
          <CardDescription className="text-center">
            {localStorage.getItem('empresa_nome') 
              ? `${localStorage.getItem('empresa_nome')}` 
              : 'Entre com seu usuário e senha para acessar o sistema'}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {loginError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {loginError}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="username">Usuário</Label>
              <Input
                id="username"
                placeholder="Seu nome de usuário"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="Sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            <div className="pt-4 pb-2">
              <div className="flex items-center space-x-2">
                <Switch 
                  id="api-login" 
                  checked={useApiLogin}
                  onCheckedChange={setUseApiLogin}
                />
                <Label htmlFor="api-login">Usar API para autenticação</Label>
              </div>
            </div>
            
            {useApiLogin && (
              <div className="space-y-4 animate-in fade-in-50">
                <div className="space-y-2">
                  <Label htmlFor="api-url">URL da API</Label>
                  <Input
                    id="api-url"
                    placeholder="https://seu-erp.com"
                    value={apiUrl}
                    onChange={(e) => setApiUrl(e.target.value)}
                    required={useApiLogin}
                  />
                  <p className="text-xs text-muted-foreground">
                    URL base do sistema sem barras no final (ex: https://suaempresa.rspcloud.com.br)
                  </p>
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Entrando...
                </>
              ) : 'Entrar'}
            </Button>
          </CardFooter>
        </form>
      </Card>
      
      <ApiDebugger />
    </div>
  );
};

export default LoginPage;
